<template>
  <div class="flex flex-col h-screen pb-safe overflow-hidden bg-content-background">
    <div
      v-if="updateAvailable"
      class="bg-v-500 text-white text-center py-2 cursor-pointer shrink-0"
      @click="installUpdate"
    >
      {{ t('update.available') }}
    </div>
    <top-bar />

    <!-- All-Day Counts full-screen mode -->
    <div v-if="screenConfig.mode === 'allday'" class="flex-1 overflow-hidden p-4">
      <all-day-counts :orders="kitchenOrderList" />
    </div>

    <!-- Horizontal mode (standard + expo) -->
    <div
      v-else-if="isHorizontalLayout && screenConfig.filter === 'open'"
      class="flex-1 overflow-x-auto overflow-y-hidden p-4"
    >
      <div class="flex gap-4 h-full">
        <products-summary
          v-if="screenConfig.showSummary"
          :orders="kitchenOrderList"
          class="shrink-0 w-80"
        />
        <kitchen-order
          v-for="order in kitchenOrderList"
          :key="order.id"
          :order="order"
          :current-time="currentTime"
          :is-expo="isExpo"
          class="shrink-0 w-80"
        />
        <div class="w-1 shrink-0 h-full"></div>
      </div>
    </div>

    <!-- Masonry / closed view -->
    <div v-else class="flex-1 overflow-x-auto overflow-y-hidden p-4">
      <div class="flex flex-col flex-wrap gap-4 h-full content-start">
        <products-summary
          v-if="screenConfig.showSummary && screenConfig.filter === 'open'"
          :orders="kitchenOrderList"
          class="w-80"
        />
        <kitchen-order
          v-for="order in kitchenOrderList"
          :key="order.id"
          :order="order"
          :current-time="currentTime"
          :is-expo="isExpo"
          class="w-80"
        />
        <div class="w-1 shrink-0 h-full"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNow } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import TopBar from '@/modules/TopBar.vue'
import { useConfigStore } from '@/store/config'
import { useKitchenOrderStore } from '@/store/kitchenOrders'
import updater from '@/updater'

import AllDayCounts from './AllDayCounts.vue'
import KitchenOrder from './KitchenOrder.vue'
import ProductsSummary from './ProductsSummary.vue'

const { t } = useI18n()

const updateAvailable = ref(false)

onMounted(async () => {
  const result = await updater.checkForUpdates()
  if (result) updateAvailable.value = true
})

async function installUpdate() {
  await updater.installUpdate()
}

const tabsStore = useKitchenOrderStore()
const configStore = useConfigStore()
const { mappedKitchenOrders } = storeToRefs(tabsStore)
const { selectedPrinterId, screenConfig } = storeToRefs(configStore)

const isExpo = computed(() => screenConfig.value.mode === 'expo')

const isHorizontalLayout = computed(() =>
  screenConfig.value.mode === 'horizontal' || screenConfig.value.mode === 'expo'
)

const kitchenOrderList = computed(() =>
  Object.values(mappedKitchenOrders.value)
    .filter(order => {
      // Expo mode: show all orders regardless of printer
      const isSelectedPrinter = isExpo.value
        ? true
        : selectedPrinterId.value !== 'all'
          ? order.printerId === selectedPrinterId.value
          : true
      const isDone = order.status === 'completed' || order.status === 'cancelled'
      const showOpen = screenConfig.value.filter === 'open'
      return isSelectedPrinter && (showOpen ? !isDone : isDone)
    })
    .sort((a, b) => {
      const timeA = new Date(a.creationTime).getTime()
      const timeB = new Date(b.creationTime).getTime()
      return timeA - timeB
    })
)

const currentTime = useNow({
  interval: 1000
})
</script>
